import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ViewContentCacheService } from '../../../../services/cache/view-content-cache.service';
import { NavigationHistoryService } from '../services/navigation-history.service';

@Component({
  selector: 'app-history-navigation',
  templateUrl: './history-navigation.component.html',
  styleUrls: ['./history-navigation.component.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
})
export class HistoryNavigationComponent implements OnInit, OnDestroy {
  @Input() isExpanded: boolean = false;

  private historyService: NavigationHistoryService = inject(NavigationHistoryService);
  private vcCache: ViewContentCacheService = inject(ViewContentCacheService);

  previousPath: string = '';
  nextPath: string = '';

  private navigationSubscription: Subscription | null = null;
  private patientLoadingMap = new Map<string, boolean>();

  ngOnInit(): void {
    this.updatePathInfo();

    this.navigationSubscription = this.historyService.getNavigationEvents().subscribe(() => {
      this.updatePathInfo();
    });
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  public onClickOnBack(): void {
    this.historyService.onClickOnBack();
  }

  public onClickOnForward(): void {
    this.historyService.onClickOnForward();
  }

  public canNavigateBack(): boolean {
    return this.historyService.canNavigateBack();
  }

  public canNavigateForward(): boolean {
    return this.historyService.canNavigateForward();
  }

  private async updatePathInfo(): Promise<void> {
    const historyStack = (this.historyService as any)['historyStack'] as string[];
    const currentPosition = (this.historyService as any)['currentPosition'] as number;

    if (historyStack && currentPosition !== undefined) {
      if (currentPosition > 0) {
        this.previousPath = await this.formatPath(historyStack[currentPosition - 1]);
      } else {
        this.previousPath = '';
      }

      if (currentPosition < historyStack.length - 1) {
        this.nextPath = await this.formatPath(historyStack[currentPosition + 1]);
      } else {
        this.nextPath = '';
      }
    }
  }

  private async formatPath(url: string): Promise<string> {
    const urlParts = url.split('/').filter(part => part);
    if (urlParts.length === 0) {
      return 'Home';
    }

    if (urlParts.length > 1 && !/^\d+$/.test(urlParts[1])) {
      const listId = decodeURIComponent(urlParts[1]);
      const listName = this.historyService.getListName(listId);

      if (listName) {
        return `${this.formatUrlPart(urlParts[0])}: ${listName}`;
      }
      return `${this.formatUrlPart(urlParts[0])}: ${this.formatUrlPart(listId)}`;
    }

    const patientIndex = urlParts.findIndex(
      part => part.toLowerCase().includes('patient') || part.toLowerCase().includes('patients'),
    );

    if (patientIndex >= 0 && patientIndex < urlParts.length - 1) {
      const potentialId = urlParts[patientIndex + 1].split('/')[0];

      let tabIndex = 0;
      if (urlParts.length > patientIndex + 2) {
        const tabPart = urlParts[patientIndex + 2];
        if (/^\d+$/.test(tabPart)) {
          tabIndex = parseInt(tabPart, 10);
        }
      }

      if (this.historyService.hasPatientInfo(potentialId)) {
        const patientInfo = this.historyService.getPatientInfo(potentialId);
        if (patientInfo) {
          if (urlParts.length > patientIndex + 2 && /^\d+$/.test(urlParts[patientIndex + 2])) {
            patientInfo.tabIndex = tabIndex;
            this.historyService.setPatientInfo(potentialId, patientInfo);
          }
        }

        const formattedName = this.historyService.getFormattedPatientName(potentialId);
        return formattedName;
      }

      if (/^\d+$/.test(potentialId)) {
        try {
          if (!this.patientLoadingMap.get(potentialId)) {
            this.patientLoadingMap.set(potentialId, true);

            const patientDetails = await this.vcCache.getPatientDetails(potentialId);
            this.patientLoadingMap.set(potentialId, false);

            if (patientDetails && patientDetails.patient && patientDetails.patient.data) {
              const { name, surname } = patientDetails.patient.data;

              this.historyService.setPatientInfo(potentialId, {
                firstName: name,
                lastName: surname,
                caseId: potentialId,
                tabIndex: tabIndex,
              });

              const formattedName = this.historyService.getFormattedPatientName(potentialId);
              return formattedName;
            }
          } else {
            console.log(`Patient ${potentialId} is already being loaded`);
          }
        } catch (error) {
          console.error('Error fetching patient details:', error);
          this.patientLoadingMap.set(potentialId, false);
        }

        return 'Patient...';
      }
    }

    const lastPart = decodeURIComponent(urlParts[urlParts.length - 1]);
    return this.formatUrlPart(lastPart);
  }

  private formatUrlPart(part: string): string {
    part = decodeURIComponent(part);

    if (part.includes('?')) {
      part = part.split('?')[0];
    }

    part = part.replace(/-/g, ' ').replace(/_/g, ' ');

    part = part
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return part;
  }
}
